import React, {
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

const COLUMN_DEFINITIONS = [
  {
    key: "block_id",
    label: "Block ID",
    description: "Unique identifier for the block.",
    minWidth: 160,
    sticky: true,
  },
  {
    key: "block_status",
    label: "Block Status",
    description: "Current operational state.",
    minWidth: 180,
  },
  {
    key: "ore_classification",
    label: "Ore Classification",
    description: "Geological profile of the ore.",
    minWidth: 200,
  },
  {
    key: "p_grade",
    label: "P-grade",
    minWidth: 140,
    align: "right",
    type: "numeric",
  },
  {
    key: "moisture",
    label: "Moisture",
    minWidth: 150,
  },
  {
    key: "slimes",
    label: "Slimes",
    minWidth: 150,
  },
  {
    key: "floatability",
    label: "Floatability",
    minWidth: 170,
  },
  {
    key: "stockpile_routing",
    label: "Stockpile Routing",
    minWidth: 220,
  },
  {
    key: "finger_number",
    label: "Finger #",
    minWidth: 120,
    align: "center",
  },
  {
    key: "tonnage",
    label: "Tonnage",
    minWidth: 140,
    align: "right",
    type: "numeric",
  },
  {
    key: "hours_remaining",
    label: "Hours Remaining",
    minWidth: 160,
    align: "right",
  },
  {
    key: "product_grade",
    label: "Product Grade",
    minWidth: 170,
  },
  {
    key: "product_classification",
    label: "Product Classification",
    minWidth: 220,
  },
  {
    key: "recovery",
    label: "Recovery",
    minWidth: 140,
    align: "right",
  },
  {
    key: "recovery_classification",
    label: "Recovery Classification",
    minWidth: 220,
  },
  {
    key: "slimes_classification",
    label: "Slimes Classification",
    minWidth: 220,
  },
  {
    key: "flotation",
    label: "Flotation",
    minWidth: 160,
  },
  {
    key: "slimes_generating",
    label: "Slimes Generating",
    minWidth: 200,
  },
  {
    key: "issues",
    label: "Issues",
    minWidth: 240,
  },
  {
    key: "notes",
    label: "Notes",
    minWidth: 240,
  },
  {
    key: "geologist_date",
    label: "Geologist Date",
    minWidth: 180,
    type: "datetime",
  },
  {
    key: "metallurgist_date",
    label: "Metallurgist Date",
    minWidth: 200,
    type: "datetime",
  },
  {
    key: "allocation_date",
    label: "Allocation Date",
    minWidth: 200,
    type: "datetime",
  },
  {
    key: "mass_pull_percent",
    label: "Mass Pull %",
    minWidth: 160,
    align: "right",
  },
  {
    key: "mass_pull_classification",
    label: "Mass Pull Classification",
    minWidth: 240,
  },
  {
    key: "created_at",
    label: "Created",
    minWidth: 180,
    type: "datetime",
  },
  {
    key: "id",
    label: "Record ID",
    minWidth: 120,
    align: "center",
  },
];

const DEFAULT_VISIBLE_COLUMNS = [
  "block_id",
  "block_status",
  "stockpile_routing",
  "ore_classification",
  "p_grade",
  "hours_remaining",
  "product_grade",
  "product_classification",
  "allocation_date",
];

const FILTERABLE_FIELDS = [
  "block_status",
  "ore_classification",
  "product_grade",
  "product_classification",
  "recovery_classification",
  "slimes_classification",
  "mass_pull_classification",
  "slimes_generating",
  "floatability",
  "stockpile_routing",
];

const INITIAL_FILTER_STATE = {
  created_from: "",
  created_to: "",
  allocation_from: "",
  allocation_to: "",
  block_status: [],
  ore_classification: [],
  product_grade: [],
  product_classification: [],
  recovery_classification: [],
  slimes_classification: [],
  mass_pull_classification: [],
  slimes_generating: [],
  floatability: [],
  stockpile_routing: [],
};

const STORAGE_KEY = "kropz:block-table:view-state";
const STORAGE_VERSION = 1;

function formatNumber(value) {
  if (value === null || value === undefined || value === "") {
    return "–";
  }

  const number = Number(value);
  if (!Number.isFinite(number)) {
    return value;
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(number);
}

function formatDate(value) {
  if (!value) {
    return "–";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatCellValue(column, value) {
  if (column.type === "numeric") {
    return formatNumber(value);
  }

  if (column.type === "datetime") {
    return formatDate(value);
  }

  if (value === null || value === undefined || value === "") {
    return "–";
  }

  return value;
}

function loadStoredState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    if (parsed.version !== STORAGE_VERSION) {
      return null;
    }

    return parsed.state ?? null;
  } catch (error) {
    return null;
  }
}

function persistState(state) {
  try {
    const payload = JSON.stringify({
      version: STORAGE_VERSION,
      state,
    });
    window.localStorage.setItem(STORAGE_KEY, payload);
  } catch (error) {
    // Ignore storage errors (e.g. Safari private mode).
  }
}

function reducer(state, action) {
  switch (action.type) {
    case "init": {
      return {
        ...state,
        ...action.payload,
      };
    }
    case "setSearch":
      return {
        ...state,
        page: 1,
        search: action.payload,
      };
    case "setPage":
      return {
        ...state,
        page: action.payload,
      };
    case "setPerPage":
      return {
        ...state,
        perPage: action.payload,
        page: 1,
      };
    case "setSort":
      return {
        ...state,
        sortBy: action.payload.by,
        sortDir: action.payload.direction,
        page: 1,
      };
    case "setFilters":
      return {
        ...state,
        filters: action.payload,
        page: 1,
      };
    case "resetFilters":
      return {
        ...state,
        filters: INITIAL_FILTER_STATE,
        search: "",
        page: 1,
      };
    default:
      return state;
  }
}

function ColumnToggleButton({ column, isActive, onToggle }) {
  return (
    <button
      type="button"
      className={`block-table-column-toggle${isActive ? " is-active" : ""}`}
      onClick={() => onToggle(column.key)}
      aria-pressed={isActive}
    >
      <span
        className={`block-table-column-toggle__indicator${
          isActive ? " is-active" : ""
        }`}
        aria-hidden="true"
      >
        {isActive ? (
          <svg viewBox="0 0 24 24" role="presentation">
            <path
              d="M5 13.5 9.5 18 19 8.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" role="presentation">
            <path
              d="M6 6l12 12M18 6 6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        )}
      </span>
      <span className="block-table-column-toggle__content">
        <span className="block-table-column-toggle__label">{column.label}</span>
        <span
          className={`block-table-column-toggle__hint${
            isActive ? " is-visible" : ""
          }`}
        >
          {isActive ? "Visible" : "Hidden"}
        </span>
      </span>
    </button>
  );
}

function ColumnManager({
  allColumns,
  visibleColumns,
  onChange,
  onClose,
  anchorRef,
  prefersReducedMotion,
}) {
  const checkboxId = useId();
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (!panelRef.current) {
        return;
      }

      if (panelRef.current.contains(event.target)) {
        return;
      }

      if (anchorRef?.current && anchorRef.current.contains(event.target)) {
        return;
      }

      onClose();
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [onClose, anchorRef]);

  const handleToggle = (key) => {
    if (visibleColumns.includes(key)) {
      onChange(visibleColumns.filter((column) => column !== key));
    } else {
      onChange([...visibleColumns, key]);
    }
  };

  const handleSelectAll = () => {
    onChange(allColumns.map((column) => column.key));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleReset = () => {
    onChange(DEFAULT_VISIBLE_COLUMNS);
  };

  return (
    <div
      className={`block-table-column-manager${
        prefersReducedMotion ? "" : " block-table-column-manager--animated"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={checkboxId}
      ref={panelRef}
    >
      <header className="block-table-column-manager__header">
        <div>
          <p className="block-table-column-manager__eyebrow">Columns</p>
          <h2 id={checkboxId}>Choose the data you want to see</h2>
        </div>
        <div className="block-table-column-manager__header-actions">
          <button type="button" onClick={handleSelectAll}>
            Select all
          </button>
          <button type="button" onClick={handleClearAll}>
            Hide all
          </button>
          <button type="button" onClick={handleReset}>
            Reset
          </button>
        </div>
      </header>
      <div className="block-table-column-manager__list" role="group">
        {allColumns.map((column) => (
          <ColumnToggleButton
            key={column.key}
            column={column}
            isActive={visibleColumns.includes(column.key)}
            onToggle={handleToggle}
          />
        ))}
      </div>
      <footer className="block-table-column-manager__footer">
        <button type="button" className="block-table-button" onClick={onClose}>
          Done
        </button>
      </footer>
    </div>
  );
}

function MultiSelectFilter({
  label,
  options,
  values,
  onChange,
  badgeColor,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const id = useId();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current) {
        return;
      }

      if (containerRef.current.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleToggleOption = (option) => {
    if (values.includes(option)) {
      onChange(values.filter((value) => value !== option));
    } else {
      onChange([...values, option]);
    }
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange([]);
  };

  return (
    <div className="block-table-filter" ref={containerRef}>
      <button
        type="button"
        className={`block-table-filter__trigger${isOpen ? " is-active" : ""}`}
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={id}
      >
        <span>{label}</span>
        {values.length > 0 ? (
          <span
            className="block-table-filter__badge"
            style={badgeColor ? { backgroundColor: badgeColor } : undefined}
          >
            {values.length}
          </span>
        ) : null}
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="block-table-filter__chevron"
        >
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        {values.length > 0 ? (
          <span
            role="button"
            tabIndex={0}
            className="block-table-filter__clear"
            onClick={handleClear}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleClear(event);
              }
            }}
          >
            Clear
          </span>
        ) : null}
      </button>
      <div
        id={id}
        className={`block-table-filter__panel${isOpen ? " is-open" : ""}`}
        role="listbox"
        aria-multiselectable="true"
      >
        {options.length === 0 ? (
          <p className="block-table-filter__empty">No options</p>
        ) : (
          options.map((option) => {
            const checked = values.includes(option);
            return (
              <label
                key={option}
                className={`block-table-filter__option${
                  checked ? " is-selected" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleToggleOption(option)}
                />
                <span>{option}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

function BlockTable() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [state, dispatch] = useReducer(reducer, {
    search: "",
    page: 1,
    perPage: 25,
    sortBy: "created_at",
    sortDir: "DESC",
    filters: INITIAL_FILTER_STATE,
  });
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0,
  });
  const [facets, setFacets] = useState({});
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false);
  const [isInitialised, setIsInitialised] = useState(false);
  const columnManagerButtonRef = useRef(null);
  const [headingActionsContainer, setHeadingActionsContainer] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const stored = loadStoredState();
    if (stored) {
      if (stored.visibleColumns && Array.isArray(stored.visibleColumns)) {
        setVisibleColumns(
          stored.visibleColumns.filter((key) =>
            COLUMN_DEFINITIONS.some((column) => column.key === key)
          )
        );
      }

      dispatch({ type: "init", payload: stored.queryState ?? {} });
      setSelectedPreset(stored.selectedPreset ?? null);
    }

    setIsInitialised(true);
  }, []);

  useEffect(() => {
    if (!isInitialised) {
      return;
    }

    persistState({
      visibleColumns,
      queryState: state,
      selectedPreset,
    });
  }, [visibleColumns, state, selectedPreset, isInitialised]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const container = document.querySelector("[data-page-heading-actions]");
    setHeadingActionsContainer(container);

    return () => {
      setHeadingActionsContainer(null);
    };
  }, []);

  const requestKey = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(state.page));
    params.set("per_page", String(state.perPage));
    params.set("sort_by", state.sortBy);
    params.set("sort_dir", state.sortDir);

    if (state.search.trim() !== "") {
      params.set("search", state.search.trim());
    }

    Object.entries(state.filters).forEach(([key, value]) => {
      if (!value) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item) => {
          params.append(`${key}[]`, item);
        });
      } else if (value !== "") {
        params.set(key, value);
      }
    });

    return params.toString();
  }, [state]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadData() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/index.php?resource=block-table&${requestKey}`,
          {
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Request failed with ${response.status}`);
        }

        const payload = await response.json();
        if (!payload || payload.status !== "ok") {
          throw new Error(payload?.message || "Unexpected API response");
        }

        const entries = payload.data?.entries ?? [];
        setData(entries);
        setPagination(payload.data?.pagination ?? {
          page: state.page,
          per_page: state.perPage,
          total: entries.length,
          total_pages: 1,
        });
        setFacets(payload.data?.facets ?? {});
      } catch (fetchError) {
        if (fetchError.name === "AbortError") {
          return;
        }
        setError(fetchError.message || "Unable to load block data");
      } finally {
        setIsLoading(false);
      }
    }

    loadData();

    return () => {
      controller.abort();
    };
  }, [requestKey, state.page, state.perPage, state.sortBy, state.sortDir]);

  useEffect(() => {
    async function loadPresets() {
      try {
        const response = await fetch("/api/index.php?resource=block-table-presets");
        if (!response.ok) {
          throw new Error(`Unable to load presets (${response.status})`);
        }

        const payload = await response.json();
        if (!payload || payload.status !== "ok") {
          throw new Error(payload?.message || "Unexpected presets response");
        }

        const available = payload.data?.presets ?? [];
        setPresets(available);
      } catch (presetError) {
        // Surface the error in console but keep UI functional.
        // eslint-disable-next-line no-console
        console.error(presetError);
      }
    }

    loadPresets();
  }, []);

  const orderedColumns = useMemo(() => {
    const columnMap = new Map(
      COLUMN_DEFINITIONS.map((column) => [column.key, column])
    );
    return visibleColumns
      .map((key) => columnMap.get(key))
      .filter(Boolean);
  }, [visibleColumns]);

  const columnCount = Math.max(orderedColumns.length, 1);

  const hiddenColumnCount = COLUMN_DEFINITIONS.length - orderedColumns.length;

  const handleSort = (column) => {
    if (!column) {
      return;
    }

    if (state.sortBy === column.key) {
      const nextDirection = state.sortDir === "ASC" ? "DESC" : "ASC";
      dispatch({
        type: "setSort",
        payload: { by: column.key, direction: nextDirection },
      });
    } else {
      dispatch({
        type: "setSort",
        payload: { by: column.key, direction: "ASC" },
      });
    }
  };

  const handlePerPageChange = (event) => {
    dispatch({ type: "setPerPage", payload: Number(event.target.value) });
  };

  const handleResetFilters = () => {
    dispatch({ type: "resetFilters" });
  };

  const handleFilterChange = (key, value) => {
    dispatch({
      type: "setFilters",
      payload: {
        ...state.filters,
        [key]: value,
      },
    });
  };

  const handleDateFilterChange = (key, value) => {
    handleFilterChange(key, value);
  };

  const handleSelectPreset = (event) => {
    const presetId = event.target.value || null;
    setSelectedPreset(presetId);

    if (!presetId) {
      setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
      return;
    }

    const preset = presets.find((item) => item.id === presetId);
    if (!preset) {
      return;
    }

    if (Array.isArray(preset.columns) && preset.columns.length > 0) {
      setVisibleColumns(
        preset.columns.filter((key) =>
          COLUMN_DEFINITIONS.some((column) => column.key === key)
        )
      );
    }

    if (preset.filters && typeof preset.filters === "object") {
      dispatch({
        type: "setFilters",
        payload: {
          ...INITIAL_FILTER_STATE,
          ...preset.filters,
        },
      });
    }
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      return;
    }

    const headers = orderedColumns.map((column) => column.label);
    const rows = data.map((row) =>
      orderedColumns
        .map((column) => {
          const raw = row[column.key];
          const formatted = formatCellValue(column, raw);
          const text = typeof formatted === "string" ? formatted : `${formatted}`;
          if (text.includes(",") || text.includes("\"")) {
            return `"${text.replace(/"/g, '""')}"`;
          }
          return text;
        })
        .join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
    link.download = `block-table-${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderSortIcon = (column) => {
    if (state.sortBy !== column.key) {
      return (
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="block-table-sort-icon"
        >
          <path
            d="M8 9l4-4 4 4M16 15l-4 4-4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      );
    }

    const isAscending = state.sortDir === "ASC";
    return (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={`block-table-sort-icon is-active ${
          isAscending ? "is-asc" : "is-desc"
        }`}
      >
        <path
          d="M12 5l4 4H8l4-4zm0 14l-4-4h8l-4 4z"
          fill="currentColor"
        />
      </svg>
    );
  };

  const handleSearchChange = (event) => {
    dispatch({ type: "setSearch", payload: event.target.value });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      dispatch({ type: "setPage", payload: 1 });
    }
  };

  const handleNextPage = () => {
    if (state.page < pagination.total_pages) {
      dispatch({ type: "setPage", payload: state.page + 1 });
    }
  };

  const handlePreviousPage = () => {
    if (state.page > 1) {
      dispatch({ type: "setPage", payload: state.page - 1 });
    }
  };

  const filterChips = useMemo(() => {
    const chips = [];
    FILTERABLE_FIELDS.forEach((field) => {
      const values = state.filters[field];
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          chips.push({ key: `${field}:${value}`, label: `${value}` });
        });
      }
    });

    [
      ["created_from", "Created from"],
      ["created_to", "Created to"],
      ["allocation_from", "Allocation from"],
      ["allocation_to", "Allocation to"],
    ].forEach(([field, label]) => {
      const value = state.filters[field];
      if (value) {
        chips.push({ key: `${field}:${value}`, label: `${label} ${value}` });
      }
    });

    if (state.search.trim() !== "") {
      chips.push({ key: `search:${state.search}`, label: `Search: ${state.search}` });
    }

    return chips;
  }, [state.filters, state.search]);

  const toolbar = (
    <div className="block-table-toolbar block-table-toolbar--inline">
      <div className="block-table-toolbar__start">
        <div className="block-table-search" role="search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M21 21l-4.35-4.35m1.19-4.84a6 6 0 1 1-12 0 6 6 0 0 1 12 0z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            placeholder="Search blocks, statuses, notes…"
            value={state.search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            aria-label="Search blocks"
          />
        </div>
        <button
          type="button"
          className={`block-table-button block-table-button--ghost${
            isFiltersOpen ? " is-active" : ""
          }`}
          onClick={() => setIsFiltersOpen((current) => !current)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4 6h16M7 12h10m-6 6h2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Filters
          {filterChips.length > 0 ? (
            <span className="block-table-button__badge">{filterChips.length}</span>
          ) : null}
        </button>
      </div>
      <div className="block-table-toolbar__end">
        <div className="block-table-preset">
          <select
            id="block-table-preset-select"
            aria-label="Select a saved table view"
            value={selectedPreset || ""}
            onChange={handleSelectPreset}
          >
            <option value="">Custom view</option>
            {presets.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="block-table-button block-table-button--ghost"
          onClick={() => setIsColumnManagerOpen(true)}
          ref={columnManagerButtonRef}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M5 5h14v3H5zm3 6h11v3H8zm5 6h6v3h-6z"
              fill="currentColor"
            />
          </svg>
          Columns
          {hiddenColumnCount > 0 ? (
            <span className="block-table-button__badge">{hiddenColumnCount}</span>
          ) : null}
        </button>
        <button
          type="button"
          className="block-table-button"
          onClick={handleExport}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Export CSV
        </button>
      </div>
    </div>
  );

  return (
    <div className="block-table-page">
      {headingActionsContainer
        ? createPortal(toolbar, headingActionsContainer)
        : toolbar}

      <div
        className={`block-table-filters${
          isFiltersOpen ? " is-open" : ""
        }${prefersReducedMotion ? "" : " block-table-filters--animated"}`}
      >
        <div className="block-table-filters__grid">
          <div className="block-table-filter-group">
            <span className="block-table-filter-group__label">Created date</span>
            <div className="block-table-filter-group__controls">
              <label>
                <span>From</span>
                <input
                  type="date"
                  value={state.filters.created_from}
                  onChange={(event) =>
                    handleDateFilterChange("created_from", event.target.value)
                  }
                />
              </label>
              <label>
                <span>To</span>
                <input
                  type="date"
                  value={state.filters.created_to}
                  onChange={(event) =>
                    handleDateFilterChange("created_to", event.target.value)
                  }
                />
              </label>
            </div>
          </div>
          <div className="block-table-filter-group">
            <span className="block-table-filter-group__label">Allocation date</span>
            <div className="block-table-filter-group__controls">
              <label>
                <span>From</span>
                <input
                  type="date"
                  value={state.filters.allocation_from}
                  onChange={(event) =>
                    handleDateFilterChange(
                      "allocation_from",
                      event.target.value
                    )
                  }
                />
              </label>
              <label>
                <span>To</span>
                <input
                  type="date"
                  value={state.filters.allocation_to}
                  onChange={(event) =>
                    handleDateFilterChange("allocation_to", event.target.value)
                  }
                />
              </label>
            </div>
          </div>
          {FILTERABLE_FIELDS.map((field) => (
            <MultiSelectFilter
              key={field}
              label={
                COLUMN_DEFINITIONS.find((column) => column.key === field)?.label ||
                field
              }
              options={facets[field] || []}
              values={state.filters[field]}
              onChange={(values) => handleFilterChange(field, values)}
            />
          ))}
        </div>
        <div className="block-table-filters__footer">
          <button
            type="button"
            className="block-table-button block-table-button--ghost"
            onClick={handleResetFilters}
          >
            Reset filters
          </button>
        </div>
      </div>

      {filterChips.length > 0 ? (
        <div className="block-table-active-filters" aria-live="polite">
          {filterChips.map((chip) => (
            <span key={chip.key} className="block-table-chip">
              {chip.label}
            </span>
          ))}
        </div>
      ) : null}

      <div className="block-table-container">
        <div className="block-table-scroll">
          <table className="block-table" role="grid">
            <thead>
              <tr>
                {orderedColumns.length === 0 ? (
                  <th scope="col" className="is-center">
                    <span>Select at least one column to display data</span>
                  </th>
                ) : (
                  orderedColumns.map((column) => {
                    const headingClassName = [
                      column.align ? `is-${column.align}` : "",
                      column.sticky ? "is-sticky" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        style={{ minWidth: `${column.minWidth || 140}px` }}
                        className={headingClassName}
                      >
                        <button
                          type="button"
                          className="block-table__sort-button"
                          onClick={() => handleSort(column)}
                        >
                          <span>{column.label}</span>
                          {renderSortIcon(column)}
                        </button>
                      </th>
                    );
                  })
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columnCount} className="block-table__loading">
                    <div className="block-table__spinner" aria-hidden="true" />
                    <span>Loading data…</span>
                  </td>
                </tr>
              ) : null}
              {!isLoading && error ? (
                <tr>
                  <td colSpan={columnCount} className="block-table__error">
                    <strong>We couldn’t load the block data.</strong>
                    <span>{error}</span>
                    <button
                      type="button"
                      className="block-table-button"
                      onClick={() => dispatch({ type: "setPage", payload: state.page })}
                    >
                      Try again
                    </button>
                  </td>
                </tr>
              ) : null}
              {!isLoading && !error && data.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="block-table__empty">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M4 7h16M4 12h16M4 17h16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <p>No blocks match the current filters.</p>
                    <button
                      type="button"
                      className="block-table-button block-table-button--ghost"
                      onClick={handleResetFilters}
                    >
                      Reset filters
                    </button>
                  </td>
                </tr>
              ) : null}
              {!isLoading && !error && orderedColumns.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="block-table__empty">
                    <p>Use the column settings to choose which metrics to display.</p>
                  </td>
                </tr>
              ) : null}
              {!isLoading && !error && orderedColumns.length > 0
                ? data.map((row) => (
                    <tr key={row.id ?? row.block_id}>
                      {orderedColumns.map((column) => (
                        <td
                          key={column.key}
                          data-column={column.label}
                          className={column.align ? `is-${column.align}` : undefined}
                        >
                          {formatCellValue(column, row[column.key])}
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="block-table-pagination">
        <div className="block-table-pagination__controls">
          <button
            type="button"
            className="block-table-button block-table-button--ghost"
            onClick={handlePreviousPage}
            disabled={state.page === 1 || isLoading}
          >
            Previous
          </button>
          <span className="block-table-pagination__status">
            Page {pagination.page} of {Math.max(1, pagination.total_pages)}
          </span>
          <button
            type="button"
            className="block-table-button block-table-button--ghost"
            onClick={handleNextPage}
            disabled={state.page >= pagination.total_pages || isLoading}
          >
            Next
          </button>
        </div>
        <div className="block-table-pagination__per-page">
          <label htmlFor="block-table-per-page">Rows per page</label>
          <select
            id="block-table-per-page"
            value={state.perPage}
            onChange={handlePerPageChange}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isColumnManagerOpen ? (
        <ColumnManager
          allColumns={COLUMN_DEFINITIONS}
          visibleColumns={visibleColumns}
          onChange={setVisibleColumns}
          onClose={() => setIsColumnManagerOpen(false)}
          anchorRef={columnManagerButtonRef}
          prefersReducedMotion={prefersReducedMotion}
        />
      ) : null}
    </div>
  );
}

export default BlockTable;
